import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Award, CheckCircle2, Loader2, RotateCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { api, formatApiError } from "../lib/api";
import { useLearning, courseStats } from "../lib/learning";
import NotFound from "./NotFound";

function Question({ q, i, value, onChange, result }) {
  return (
    <fieldset className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`quiz-question-${q.id}`}>
      <legend className="sr-only">Question {i + 1}</legend>
      <p className="font-num text-xs text-slate-400">Question {i + 1}</p>
      <p className="mt-2 font-semibold text-navy">{q.question}</p>
      <div className="mt-4 space-y-2">
        {q.options.map((o, j) => {
          const picked = value === j;
          const isAnswer = result?.answer === j;
          const tone = result && result.answer !== undefined
            ? (isAnswer ? "border-mint bg-mint/10" : picked ? "border-red-300 bg-red-50" : "border-slate-200")
            : picked ? "border-navy bg-navy/5" : "border-slate-200 hover:border-slate-300";
          return (
            <label key={j} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${tone}`} data-testid={`quiz-${q.id}-opt-${j}`}>
              <input type="radio" name={q.id} className="mt-0.5 accent-[#071426]" checked={picked} onChange={() => onChange(j)} disabled={!!result} />
              <span className="text-slate-700">{o}</span>
            </label>
          );
        })}
      </div>
      {result && (
        <div className={`mt-4 flex gap-2 text-sm ${result.correct ? "text-mint-600" : "text-red-600"}`} data-testid={`quiz-result-${q.id}`}>
          {result.correct ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
          <span>{result.correct ? "Correct." : "Not quite."} {result.explanation && <span className="text-slate-600">{result.explanation}</span>}</span>
        </div>
      )}
    </fieldset>
  );
}

function ResultPanel({ res, slug, certificate, onRetry }) {
  return (
    <div className={`rounded-3xl p-8 ${res.passed ? "bg-navy text-white" : "border border-red-200 bg-red-50"}`} data-testid="quiz-result-panel">
      <p className={`text-sm font-semibold uppercase tracking-wider ${res.passed ? "text-gold" : "text-red-600"}`}>{res.passed ? "Passed" : "Not passed yet"}</p>
      <p className={`mt-2 text-4xl font-bold ${res.passed ? "" : "text-navy"}`} data-testid="quiz-score">{res.score}/{res.total} · {res.pct}%</p>
      <p className={`mt-2 text-sm ${res.passed ? "text-slate-300" : "text-slate-600"}`}>
        {res.passed ? "Well done. Your Quantora certificate is ready." : `You need ${res.pass_pct}% to pass. Review the lessons for the questions you missed and try again.`}
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        {res.passed && certificate && <Link to={`/certificates/${certificate}`} data-testid="quiz-view-certificate-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy"><Award size={16} /> View certificate</Link>}
        {!res.passed && <button onClick={onRetry} data-testid="quiz-retry-btn" className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white"><RotateCcw size={15} /> Try again</button>}
        <Link to={`/learn/${slug}`} data-testid="quiz-back-course-btn" className={`rounded-full border px-6 py-3 text-sm font-semibold ${res.passed ? "border-white/30 text-white" : "border-slate-300 text-navy"}`}>Back to course</Link>
      </div>
    </div>
  );
}

export default function QuizPage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [missing, setMissing] = useState(false);
  const [answers, setAnswers] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user, progress, certificates, submitQuiz } = useLearning();
  useEffect(() => { api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setMissing(true)); }, [slug]);

  if (missing) return <NotFound />;
  if (!course) return <div className="min-h-[60vh] bg-slate-50" data-testid="quiz-loading" />;
  if (!course.quiz?.question_count) return <NotFound />;
  const s = courseStats(course, progress[slug]);
  const qs = course.quiz.questions;
  const byId = Object.fromEntries((res?.results || []).map((r) => [r.id, r]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await submitQuiz(slug, answers);
      setRes(data);
      data.passed ? toast.success(`Passed with ${data.pct}%`) : toast.error(`Scored ${data.pct}%. ${data.pass_pct}% needed to pass.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const retry = () => { setRes(null); setAnswers({}); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <>
      <Seo title={`Final Quiz – ${course.title}`} description={course.summary} path={`/learn/${slug}/quiz`} noindex />
      <section className="bg-slate-100/70 py-14">
        <div className="container-q max-w-3xl">
          <Link to={`/learn/${slug}`} data-testid="quiz-course-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy"><ArrowLeft size={15} /> {course.title}</Link>
          <h1 className="mt-4 text-3xl font-bold text-navy sm:text-4xl" data-testid="quiz-title">Final quiz</h1>
          <p className="mt-3 text-slate-600">{course.quiz.question_count} questions · pass mark {course.quiz.pass_pct}%. Pass to earn your Quantora certificate.</p>
          {s.quizPassed && !res && <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-mint/15 px-4 py-2 text-sm font-semibold text-mint-600" data-testid="quiz-already-passed"><CheckCircle2 size={16} /> You have already passed this quiz.</p>}
          <div className="mt-8 space-y-5">
            {!user && <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="quiz-login-required">Sign in and complete every lesson to take the final quiz. <Link to="/login" className="font-semibold text-navy underline" data-testid="quiz-login-btn">Sign in</Link></div>}
            {user && !s.lessonsDone && (
              <div className="rounded-2xl border border-gold/40 bg-gold-50 p-6 text-sm text-slate-700" data-testid="quiz-locked">
                Finish all lessons first ({s.done.length} of {s.total} done). <Link to={s.nextPath} className="font-semibold text-navy underline" data-testid="quiz-locked-continue">Continue learning</Link>
              </div>
            )}
            {res && <ResultPanel res={res} slug={slug} certificate={res.certificate_id || certificates[slug]?.id} onRetry={retry} />}
            {user && s.lessonsDone && (
              <form onSubmit={submit} className="space-y-5" data-testid="quiz-form">
                {qs.map((q, i) => <Question key={q.id} q={q} i={i} value={answers[q.id]} result={byId[q.id]} onChange={(j) => setAnswers({ ...answers, [q.id]: j })} />)}
                {!res && (
                  <button disabled={busy || Object.keys(answers).length < qs.length} data-testid="quiz-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-mint px-8 py-3.5 text-sm font-semibold text-navy disabled:opacity-40">
                    {busy && <Loader2 size={16} className="animate-spin" />} Submit answers
                  </button>
                )}
              </form>
            )}
          </div>
          <p className="mt-10 text-xs text-slate-500">Quantora certificates recognise completion of educational content. They are not professional, academic or regulatory qualifications.</p>
        </div>
      </section>
    </>
  );
}
